import { MACRONUTRIENT_STATUS_LABELS } from '../constants/maintenance.constants';
import type {
	NutrientCoverageStatus,
	SimpleDataTableColumn,
	SimpleDataTableRow,
} from '../components/Tables/SimpleDataTable/SimpleDataTable';
import type { MaintenanceNutritionTableResponse } from '../types';

export interface NutrientStatusTableGroup {
	title: string;
	columns: SimpleDataTableColumn[];
	rows: SimpleDataTableRow[];
}

const MIN_MET_PERCENTAGE = 90;
const MAX_MET_PERCENTAGE = 110;

const classifyNutrientCoverage = (percentage: number): NutrientCoverageStatus => {
	if (percentage === 100) return 'complete';
	if (percentage >= MIN_MET_PERCENTAGE && percentage <= MAX_MET_PERCENTAGE) return 'met';
	return percentage < MIN_MET_PERCENTAGE ? 'below' : 'above';
};

const NUTRIENT_STATUS_COLUMNS: SimpleDataTableColumn[] = [
	{ key: 'nutrient', label: 'NUTRIENTE', emphasis: 'strong' },
	{ key: 'annualDemand', label: 'DEMANDA ANUAL', emphasis: 'default' },
	{ key: 'supplied', label: 'FORNECIDO', emphasis: 'muted' },
	{ key: 'coverage', label: 'COBERTURA', variant: 'coverage' },
	{ key: 'metStatus', label: 'DEMANDA ATENDIDA', variant: 'status' },
];

const displayValue = (value: number | null | undefined): string =>
	typeof value === 'number' && Number.isFinite(value) ? `${value} kg/ha` : '—';

export const mapMaintenanceAnnualNutritionRows = (
	response?: MaintenanceNutritionTableResponse
): NutrientStatusTableGroup[] => {
	if (!response?.tableAnnual) return [];

	const rows: SimpleDataTableRow[] = MACRONUTRIENT_STATUS_LABELS.flatMap(({ key, label }) => {
		const values = response.tableAnnual[key];
		if (!values) return [];

		const [annualDemand, supplied, percentage] = values;
		const hasCoverage = typeof percentage === 'number' && Number.isFinite(percentage);

		return [
			{
				id: key,
				cells: {
					nutrient: label,
					annualDemand: displayValue(annualDemand),
					supplied: displayValue(supplied),
				},
				coverage: hasCoverage
					? {
							coverage: { percentage: percentage as number, status: classifyNutrientCoverage(percentage as number) },
							metStatus: { percentage: percentage as number, status: classifyNutrientCoverage(percentage as number) },
						}
					: undefined,
			},
		];
	});

	if (rows.length === 0) return [];

	return [{ title: 'Macronutrientes', columns: NUTRIENT_STATUS_COLUMNS, rows }];
};
